import { Calendar, HardDrive, LogOut, Sun, Moon, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import ChatBot from "@/components/ChatBot";

const apps = [
  {
    name: "Calendrier",
    description: "Gérer vos événements et rendez-vous",
    icon: Calendar,
    href: "https://lamaisondepepito.duckdns.org/calendar/",
    color: "from-violet-500 to-purple-600",
    iconBg: "bg-violet-500/10 dark:bg-violet-500/20",
    iconColor: "text-violet-600 dark:text-violet-400",
  },
  {
    name: "Drive",
    description: "Stocker et partager vos fichiers",
    icon: HardDrive,
    href: "https://lamaisondepepito.duckdns.org/drive/",
    color: "from-blue-500 to-cyan-600",
    iconBg: "bg-blue-500/10 dark:bg-blue-500/20",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
];

export default function HomePage({ onLogout }) {
  const [userName, setUserName] = useState(() => {
    return localStorage.getItem("home-user-name") || "Micka\u00ebl";
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isDark = document.documentElement.classList.contains("dark");

  const handleUserNameChange = (name) => {
    setUserName(name);
    localStorage.setItem("home-user-name", name);
    setDropdownOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTheme = () => {
    document.documentElement.classList.toggle("dark");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm font-[Manrope]">
                LS
              </span>
            </div>
            <h1 className="text-xl font-bold font-[Manrope]">La Suite</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {isDark ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={onLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-3xl w-full mx-auto">
          <div className="text-center mb-12 mt-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <h2 className="text-3xl font-bold font-[Manrope]">
                Bienvenue à la maison
              </h2>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="text-3xl font-bold font-[Manrope] bg-background text-foreground border border-border/30 rounded-xl px-3 py-0.5 focus:outline-none focus:border-border/50 cursor-pointer flex items-center gap-1"
                >
                  {userName}
                  <ChevronDown className={`w-5 h-5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-background border border-border/30 rounded-xl overflow-hidden shadow-lg z-50 min-w-full">
                    {["Micka\u00ebl", "Chlo\u00e9"].map((name) => (
                      <button
                        key={name}
                        onClick={() => handleUserNameChange(name)}
                        className={`w-full text-left px-4 py-2 text-lg font-bold font-[Manrope] cursor-pointer transition-colors ${
                          userName === name
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-muted"
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-muted-foreground text-lg">
              Sélectionnez une application pour continuer
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {apps.map((app) => (
              <a
                key={app.name}
                href={app.href}
                className="app-card group block rounded-2xl border border-border bg-card p-8 text-left no-underline hover:border-primary/30 transition-all duration-200"
              >
                <div
                  className={`w-16 h-16 rounded-2xl ${app.iconBg} flex items-center justify-center mb-5`}
                >
                  <app.icon className={`w-8 h-8 ${app.iconColor}`} />
                </div>
                <h3 className="text-xl font-semibold font-[Manrope] mb-2 text-foreground">
                  {app.name}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {app.description}
                </p>
                <div className="mt-5 flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  Ouvrir →
                </div>
              </a>
            ))}
          </div>

          {/* Séparateur */}
          <div className="max-w-2xl mx-auto mt-14 mb-4">
            <div className="h-px bg-border" />
          </div>

          {/* ChatBot Pépito */}
          <ChatBot userName={userName} />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-4">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm text-muted-foreground">
          La Suite © {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}
